import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { PROPS_TO_DEHYDRATE } from '@/trendData/baseItem.constants';
import { BaseItemStore } from '@/trendData/baseItem.store';
import {
  type ConditionLayoutUpdate,
  calculateConditionLaneChanges,
  calculateConditionOrderInLane,
  calculateConditionOverlays,
  getOrderInLaneOptions as getOrderInLaneOptionsUtil,
  getOverlayOptions as getOverlayOptionsUtil,
} from '@/trendData/trendConditionLayout.utilities';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { InitializeMode } from '@/core/flux.service';
import type { Item } from '@/trendData/baseItem.store';
import type { ConditionItem } from '@/trendData/trendData.types';
import type { ConditionMonitorMetadataOutputV1 } from '@seeqdev/api/model/ConditionMonitorMetadataOutputV1';

export type TrendConditionStoreDehydratedState = Pick<BaseItemStore<Item>, 'items'> & {
  items: Partial<ConditionItem>[];
};

/**
 * A store for containing Conditions. Conditions are used to generate capsules.
 *
 * This store is augmented with additional functionality from BaseItemStore.
 */
export class TrendConditionStore extends BaseItemStore<ConditionItem> {
  static readonly storeName = 'sqTrendConditionStore';

  /** Condition monitor metadata from the last TREND_SET_MONITORED_CONDITION_IDS dispatch. */
  get conditionMonitorMetadataList(): ConditionMonitorMetadataOutputV1[] {
    return this.state.get('conditionMonitorMetadataList') ?? [];
  }

  initialize(initializeMode: InitializeMode): void {
    super.initialize(initializeMode);
    this.state.set('conditionMonitorMetadataList', []);
  }

  /**
   * Adds a Condition item.
   *
   * @param {Object} payload - Object container for arguments
   */
  private addCondition = (payload: {
    /** ID of the new Condition */
    id: string;
    /** Name of the new Condition */
    name: string;
    /** Color hex code (e.g. #CCCCCC) */
    color: string;
    /** Lane for the new Condition */
    lane: number;
    /** Represents the thickness of capsules in new Condition */
    lineWidth: number;
  }): void => {
    const props = _.pick(payload, [
      'color',
      'lane',
      'lineWidth',
      'overlay',
      'laneReservation',
      'orderInLane',
      'orderInLanePriority',
    ]);
    this.state.push('items', this.createCondition(payload.id, payload.name, props));
  };

  /**
   * Private helper function to add a condition to the store using the specified properties.
   *
   * @param {String} id - ID to use for the new Condition
   * @param {String} name - Name to use for the new Condition
   * @param {Object} props - Object containing properties to apply to the new calculation
   *
   * @returns Newly created Condition object.
   */
  private createCondition = (
    id: string,
    name: string,
    props: Partial<Omit<ConditionItem, 'id' | 'name' | 'itemType'>>,
  ) => {
    return this.createItem(
      id,
      name,
      ITEM_TYPES.CONDITION,
      _.assign(
        {
          dataStatus: ITEM_DATA_STATUS.INITIALIZING,
        },
        props,
      ),
    );
  };

  /*
   Find the number of items in the same lane, then the options are the lane numbers from 1 to the number of
   items in the lane, plus the unoverlay option
   */
  public getOverlayOptions(lane: number, items?: ConditionItem[]): ReactSelectOption[] {
    const itemsToCheck = items || this.items;
    return getOverlayOptionsUtil(lane, itemsToCheck);
  }

  public getOrderInLaneOptions(lane: number, items?: ConditionItem[]): ReactSelectOption[] {
    const itemsToCheck = items || this.items;
    return getOrderInLaneOptionsUtil(lane, itemsToCheck);
  }

  /**
   * Generates the overlays for all conditions in the same lane as the specified item, accounting for adjustments
   * that need to be made due to the addition of the specified item.
   */
  getConditionOverlays(item: ConditionItem, overlay: number | undefined): ConditionLayoutUpdate[] {
    return calculateConditionOverlays(item, overlay, this.items);
  }

  getConditionOrderInLane(item: ConditionItem, newOrderInLane: number | undefined): ConditionLayoutUpdate[] {
    return calculateConditionOrderInLane(item, newOrderInLane, this.items);
  }

  /**
   * Handles lane changes by renumbering overlay/orderInLane values for affected lanes.
   * When a condition moves from oldLane to newLane:
   * - Conditions remaining in oldLane get renumbered (1..N)
   * - Condition moving to newLane gets assigned next overlay/orderInLane value if newLane has them set
   * - Returns updates for ALL affected conditions in both lanes
   */
  getConditionLaneChanges(item: ConditionItem, newLane: number): ConditionLayoutUpdate[] {
    return calculateConditionLaneChanges(item, newLane, this.items);
  }

  protected readonly handlers = {
    ...super.baseHandlers,
    TREND_ADD_CAPSULE_SET: this.addCondition,
    TREND_SET_MONITORED_CONDITION_IDS: ({
      conditionIds,
      conditionMonitorMetadataList,
    }: {
      conditionIds: string[];
      conditionMonitorMetadataList: ConditionMonitorMetadataOutputV1[];
    }) => {
      const initialIdMap = _.reduce(
        conditionIds,
        (ids, conditionId) => {
          ids.set(conditionId, { notifications: [], vantages: [] });

          return ids;
        },
        new Map<string, { notifications: string[]; vantages: string[] }>(),
      );
      const idMap = conditionMonitorMetadataList
        .flatMap((conditionMonitorMetadata) =>
          conditionMonitorMetadata.conditionIds.map((id) => ({
            conditionId: id,
            conditionMonitorMetadata,
          })),
        )
        .reduce((ids, { conditionId, conditionMonitorMetadata }) => {
          const monitoredCondition = ids.get(conditionId);
          if (monitoredCondition) {
            conditionMonitorMetadata.workbook?.type === SeeqNames.Types.Vantage
              ? monitoredCondition.vantages.push(conditionMonitorMetadata.id)
              : monitoredCondition.notifications.push(conditionMonitorMetadata.id);
          }

          return ids;
        }, initialIdMap);

      idMap.forEach(({ notifications, vantages }, conditionId) => {
        this.setProperties({ id: conditionId, notifications, vantages });
      });
      this.state.set('conditionMonitorMetadataList', conditionMonitorMetadataList);
    },
  };

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate(): TrendConditionStoreDehydratedState {
    return {
      items: _.chain(this.state.get('items'))
        .filter(this.shouldDehydrateItem)
        .map((item) => _.pick(item, PROPS_TO_DEHYDRATE as any))
        .value(),
    };
  }

  /**
   * Re-creates the conditions.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<TrendConditionStoreDehydratedState>): void {
    this.state.set(
      'items',
      _.map(dehydratedState.items, (item) => this.createCondition(item.id!, item.name!, _.omit(item, 'id', 'name'))),
    );
  }
}
